/**
 * Auth0 Service
 * 
 * Handles social login authentication via Auth0.
 * SDK is lazy-loaded on first use to avoid unnecessary overhead.
 * 
 * @example
 * ```typescript
 * import { auth0Service } from '../services/Auth0Service';
 * 
 * const user = await auth0Service.login();
 * console.log('Logged in:', user.email, 'via', user.provider);
 * ```
 */

import { Logger } from './Logger';
import { ToasterService } from './ToasterService';
import { oemConfig } from './OEMConfig';

const logger = Logger.scope('Auth0Service');

// Auth0 SPA SDK CDN URL
const AUTH0_SDK_URL = 'https://cdn.auth0.com/js/auth0-spa-js/2.0/auth0-spa-js.production.js';

/**
 * User information returned from Auth0 after successful authentication
 */
export interface Auth0User {
  /** Auth0 user ID (e.g., "google-oauth2|123456789") */
  sub: string;
  /** User's email address */
  email: string;
  /** User's display name */
  name: string;
  /** Profile picture URL */
  picture?: string;
  /** User's nickname */
  nickname?: string;
  /** Identity provider (e.g., "google-oauth2", "windowslive", "apple") */
  provider: string;
  /** Raw ID token for API verification */
  authToken: string;
  /** Raw token claims object for backend API - contains all Auth0 fields */
  rawClaims: Record<string, unknown>;
}

/**
 * Auth0 configuration
 */
interface Auth0Config {
  domain: string;
  clientId: string;
  redirectUri: string;
}

/**
 * Auth0 client interface (from SDK)
 */
interface Auth0Client {
  loginWithPopup(options?: { authorizationParams?: { prompt?: string; redirect_uri?: string } }): Promise<void>;
  logout(options?: { logoutParams?: { returnTo: string }; openUrl?: boolean }): Promise<void>;
  getUser(): Promise<any>;
  getIdTokenClaims(): Promise<{ __raw: string } | undefined>;
}

/**
 * Global window interface extension for Auth0 SDK
 */
declare global {
  interface Window {
    auth0?: {
      createAuth0Client(config: {
        domain: string;
        clientId: string;
        authorizationParams: { redirect_uri: string };
        cacheLocation: string;
      }): Promise<Auth0Client>;
    };
  }
}

class Auth0ServiceImpl {
  private client: Auth0Client | null = null;
  private sdkLoaded = false;
  private sdkLoading = false;
  private config: Auth0Config;

  constructor() {
    // Base64 encoded config (decoded at runtime for minimal obfuscation)
    this.config = {
      domain: atob('ZGV2LWJzZy51ay5hdXRoMC5jb20='),           // dev-bsg.uk.auth0.com
      clientId: atob('RkJNVUNLODN2U0NNYjZ1WTlFbTVGWkVWbEJUMkRueDM='),  // FBMUCK83vSCMb6uY9Em5FZEVlBT2Dnx3
      redirectUri: window.location.origin
    };

    // Fix for localhost development: Auth0 requires exact match
    // If running on http://localhost:5173, ensure we don't have trailing slash
    if (this.config.redirectUri.endsWith('/')) {
      this.config.redirectUri = this.config.redirectUri.slice(0, -1);
    }
  }

  /**
   * Lazy-load the Auth0 SPA SDK
   */
  private async loadSDK(): Promise<void> {
    if (this.sdkLoaded) return;
    if (this.sdkLoading) {
      // Wait for existing load to complete
      while (this.sdkLoading) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      return;
    }

    this.sdkLoading = true;
    logger.info('Loading Auth0 SPA SDK...');

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = AUTH0_SDK_URL;
      script.async = true;

      script.onload = () => {
        logger.info('Auth0 SPA SDK loaded successfully');
        this.sdkLoaded = true;
        this.sdkLoading = false;
        resolve();
      };

      script.onerror = (error) => {
        logger.error('Failed to load Auth0 SPA SDK:', error);
        this.sdkLoading = false;
        reject(new Error('Failed to load Auth0 SDK'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize the Auth0 client
   */
  private async initClient(): Promise<void> {
    if (this.client) return;

    // Override clientId from OEM config if available
    const oemClientId = oemConfig.getConfig()?.auth?.config?.auth0ClientId as string | undefined;
    if (oemClientId) {
      this.config.clientId = oemClientId;
      logger.info('Using tenant-specific Auth0 clientId');
    }

    await this.loadSDK();

    if (!window.auth0?.createAuth0Client) {
      throw new Error('Auth0 SDK not available after loading');
    }

    logger.info('Creating Auth0 client...');

    this.client = await window.auth0.createAuth0Client({
      domain: this.config.domain,
      clientId: this.config.clientId,
      authorizationParams: {
        redirect_uri: this.config.redirectUri
      },
      cacheLocation: 'memory'  // Session only - no persistence
    });

    logger.info('Auth0 client created successfully');
  }

  /**
   * Login via Auth0 popup
   * 
   * @returns Promise<Auth0User> - User information after successful login
   * @throws Error if login fails or is cancelled
   */
  async login(): Promise<Auth0User> {
    await this.initClient();

    if (!this.client) {
      throw new Error('Auth0 client not initialized');
    }

    try {
      logger.info('Opening Auth0 login popup...');

      await this.client.loginWithPopup({
        authorizationParams: {
          prompt: 'login',
          redirect_uri: this.config.redirectUri
        }
      });

      // Get user info
      const userInfo = await this.client.getUser();
      if (!userInfo) {
        throw new Error('No user info returned from Auth0');
      }

      // Get ID token for API verification
      const tokenClaims = await this.client.getIdTokenClaims();
      if (!tokenClaims) {
        throw new Error('No token claims returned from Auth0');
      }

      // Build raw claims object for backend API
      // Only include user profile fields - NOT JWT claim fields (iss, aud, iat, exp, sid, auth_time, nonce)
      const allowedFields = ['given_name', 'family_name', 'nickname', 'name', 'picture', 'updated_at', 'email', 'email_verified', 'sub'];
      const rawClaims: Record<string, unknown> = {};
      for (const key of allowedFields) {
        if (key in tokenClaims) {
          rawClaims[key] = tokenClaims[key as keyof typeof tokenClaims];
        }
      }
      // Add the raw JWT token
      rawClaims.authToken = tokenClaims.__raw;

      const user: Auth0User = {
        sub: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name || userInfo.nickname || userInfo.email,
        picture: userInfo.picture,
        nickname: userInfo.nickname,
        provider: this.extractProvider(userInfo.sub),
        authToken: tokenClaims.__raw,
        rawClaims
      };

      logger.info('Auth0 login successful:', user.email, 'via', user.provider);

      // Clean up any residual Google One Tap / credential picker iframes
      document.querySelectorAll('iframe[src*="accounts.google.com"]').forEach(el => el.remove());
      document.getElementById('credential_picker_container')?.remove();

      return user;

    } catch (error) {
      logger.error('Auth0 login failed:', error);
      throw error;
    }
  }

  /**
   * Logout from Auth0 (clear session)
   */
  async logout(): Promise<void> {
    if (!this.client) return;

    await this.client.logout({
      logoutParams: {
        returnTo: this.config.redirectUri
      },
      openUrl: false
    });

    logger.debug('Auth0 session cleared');
  }

  /**
   * Extract provider name from Auth0 sub (user ID)
   * 
   * @example "google-oauth2|123456" → "google-oauth2"
   */
  private extractProvider(sub: string): string {
    if (sub.includes('|')) {
      return sub.split('|')[0];
    }
    return sub;
  }

  /**
   * Get FontAwesome icon class for a provider
   */
  getProviderIcon(provider: string): string {
    const p = provider.toLowerCase();
    if (p.includes('google')) return 'fa-brands fa-google';
    if (p.includes('windows') || p.includes('microsoft')) return 'fa-brands fa-microsoft';
    if (p.includes('apple')) return 'fa-brands fa-apple';
    if (p.includes('github')) return 'fa-brands fa-github';
    if (p.includes('linkedin')) return 'fa-brands fa-linkedin-in';
    return 'fas fa-user';
  }

  /**
   * Get display name for a provider
   */
  getProviderName(provider: string): string {
    const p = provider.toLowerCase();
    if (p.includes('google')) return 'Google';
    if (p.includes('windows') || p.includes('microsoft')) return 'Microsoft';
    if (p.includes('apple')) return 'Apple';
    if (p.includes('github')) return 'GitHub';
    if (p.includes('linkedin')) return 'LinkedIn';
    return 'Social';
  }
}

// Singleton instance
export const auth0Service = new Auth0ServiceImpl();
